import * as api from '@/vuex/modules/roadmaps/api/roadmaps.api.js';
import { asyncQueue } from '@/vuex/modules/roadmaps/async-queue/async-queue';
import { freezeRoadmap } from '@/vuex/modules/roadmaps/utils/roadmaps.utils.js';
import { $canSub } from '@pendo/permissions';
import mapValues from 'lodash/mapValues';
import Vue from 'vue';

const state = {
    roadmaps: {},
    currentRoadmapId: null,
};

const getters = {
    list: (state) => {
        return {
            ...state.roadmaps,
            ...asyncQueue.queuedUpdates.roadmaps,
        };
    },
    currentRoadmap: (state) => state.roadmaps[state.currentRoadmapId],
    currentRoadmapVersionTimestamp: (state) => state.roadmaps[state.currentRoadmapId].lastUpdatedAt,
    editableRoadmaps: (_state, getters) =>
        Object.values(getters.list).filter((roadmap) => getters.canEditRoadmap(roadmap.id)),
    roadmapById: (_state, getters) => (roadmapId) => getters.list[roadmapId],
    isRoadmapOwnerOrOps: (state, _getters, rootState, rootGetters) => (roadmapId) => {
        const roadmapOwner = state.roadmaps[roadmapId].createdByUser.id;
        const currentUser = rootState.auth.user.id;
        const isOpsUser = rootGetters['auth/isOps'];

        return roadmapOwner === currentUser || isOpsUser;
    },
    isCurrentRoadmapViewOnly: (_state, getters) => {
        const { currentRoadmap } = getters;

        return !getters.canEditRoadmap(currentRoadmap.id);
    },
    isCurrentRoadmapRestricted: (_state, getters) => {
        const { currentRoadmap } = getters;
        const isRoadmapOwnerOrOps = getters.isRoadmapOwnerOrOps(currentRoadmap.id);
        const isRestricted = currentRoadmap.permission === 'restricted';

        return isRestricted && !isRoadmapOwnerOrOps;
    },
    isTimelineRoadmap: (_state, getters) => {
        const { currentRoadmap } = getters;

        return currentRoadmap.type === 'TIMELINE';
    },
    canEditRoadmap: (_state, getters) => (roadmapId) => {
        const { permission } = getters.roadmapById(roadmapId);

        return permission === 'edit' || permission === 'owner' || permission === 'ops';
    },
    isRoadmapCreator: () => {
        return $canSub()
            .entity('roadmap')
            .action('create');
    },
    isRoadmapPublisher: () => {
        return $canSub()
            .entity('roadmap')
            .action('publish');
    },
};

const mutations = {
    deleteByKey (state, roadmapId) {
        Vue.delete(state.roadmaps, roadmapId);
    },
    setCurrentRoadmapId (state, roadmapId) {
        state.currentRoadmapId = roadmapId;
    },
    setMap (state, { roadmaps }) {
        state.roadmaps = mapValues(roadmaps, freezeRoadmap);
    },
    setMapAtKey (state, { roadmap }) {
        const frozenRoadmap = freezeRoadmap(roadmap);
        Vue.set(state.roadmaps, roadmap.id, frozenRoadmap);
    },
    updateMap (state, { roadmaps }) {
        const roadmapsUpdated = Object.assign({}, state.roadmaps, roadmaps);
        state.roadmaps = mapValues(roadmapsUpdated, freezeRoadmap);
    },
    updateMapAtId (state, { id, roadmap }) {
        const frozenRoadmap = freezeRoadmap(Object.assign({}, state.roadmaps[id], roadmap));
        Vue.set(state.roadmaps, id, frozenRoadmap);
    },
    updateMapAtKey (state, { id, key, value }) {
        const frozenRoadmap = freezeRoadmap(Object.assign({}, state.roadmaps[id], { [key]: value }));
        Vue.set(state.roadmaps, id, frozenRoadmap);
    },
};

const actions = {
    async loadAll ({ commit }) {
        const roadmaps = await api.fetchRoadmaps();

        commit('setMap', { roadmaps });
    },
    async loadOne ({ dispatch }, { id }) {
        const roadmap = await api.fetchRoadmap(id);

        dispatch('setOne', roadmap);

        return roadmap;
    },
    async setOne ({ commit }, roadmap) {
        commit('roadmaps/swimlanes/setMap', { swimlanes: roadmap.swimlanes }, { root: true });
        commit('roadmaps/columns/setMap', { columns: roadmap.columns }, { root: true });
        commit('roadmaps/items/setMap', { items: roadmap.items }, { root: true });
        commit('setCurrentRoadmapId', roadmap.id);
        commit('setMapAtKey', { roadmap });
    },
    async createRoadmap ({ commit }, roadmapToCreate) {
        const createdRoadmap = await api.createRoadmap(roadmapToCreate);
        commit('updateMap', { roadmaps: { [createdRoadmap.id]: createdRoadmap } });

        return createdRoadmap.id;
    },
    async updateRoadmap ({ commit, state }, roadmapToUpdate) {
        const task = async () => {
            const roadmapVersionTimestamp = state.roadmaps[roadmapToUpdate.id].lastUpdatedAt;
            const payload = {
                title: roadmapToUpdate.title,
                description: roadmapToUpdate.description,
                quarterOneStartingMonth: roadmapToUpdate.quarterOneStartingMonth,
                roadmapVersionTimestamp,
            };
            const updatedRoadmap = await api.updateRoadmap(roadmapToUpdate.id, payload);

            commit('updateMapAtId', { id: updatedRoadmap.id, roadmap: updatedRoadmap });
            // update roadmap can move items if starting month is set
            commit('roadmaps/items/updateMap', { items: updatedRoadmap.items }, { root: true });

            return updatedRoadmap;
        };

        return asyncQueue.enqueue({
            task,
            updates: { [roadmapToUpdate.id]: roadmapToUpdate },
            updateType: 'roadmaps',
        });
    },

    async updateRoadmapSubscriptionPermissions ({ getters, commit }, permission) {
        const { currentRoadmap } = getters;
        const payload = {
            permission,
        };
        const updatedRoadmap = await api.updateRoadmapSubscriptionPermissions(currentRoadmap.id, payload);

        commit('setMapAtKey', { roadmap: updatedRoadmap });
    },
    async addRoadmapUserPermissions ({ getters, commit }, userPermissionsToAdd) {
        const { currentRoadmap } = getters;
        const updatedRoadmap = await api.addRoadmapUserPermissions(currentRoadmap.id, userPermissionsToAdd);

        commit('setMapAtKey', { roadmap: updatedRoadmap });
    },
    async updateRoadmapUserPermission ({ getters, commit }, userPermission) {
        const { currentRoadmap } = getters;
        const payload = {
            permission: userPermission.permission,
        };
        const updatedRoadmap = await api.updateRoadmapUserPermission(currentRoadmap.id, userPermission.userId, payload);

        commit('setMapAtKey', { roadmap: updatedRoadmap });
    },
    async removeRoadmapUserPermission ({ getters, commit }, userId) {
        const { currentRoadmap } = getters;
        const updatedRoadmap = await api.removeRoadmapUserPermission(currentRoadmap.id, userId);

        commit('setMapAtKey', { roadmap: updatedRoadmap });
    },
    setCurrentRoadmapCreatedUpdatedInfo (
        { getters, commit },
        { createdAt, createdByUser, lastUpdatedAt, lastUpdatedByUser },
    ) {
        const { currentRoadmap } = getters;
        const updatedRoadmap = Object.assign({}, currentRoadmap, {
            createdAt,
            createdByUser,
            lastUpdatedAt,
            lastUpdatedByUser,
        });
        commit('updateMapAtId', { id: currentRoadmap.id, roadmap: updatedRoadmap });
    },
    setCurrentRoadmapPublishInfo ({ getters, commit }, publishedInfo) {
        const { currentRoadmap } = getters;
        const updatedRoadmap = Object.assign({}, currentRoadmap, {
            publishedInfo,
        });
        commit('setMapAtKey', { roadmap: updatedRoadmap });
    },
    async publishRoadmap ({ getters, dispatch }, roadmapId) {
        const task = async () => {
            const updatedRoadmap = await api.publishRoadmap(roadmapId);

            dispatch('setOne', updatedRoadmap);

            return updatedRoadmap;
        };

        const roadmap = getters.roadmapById(roadmapId);

        return asyncQueue.enqueue({
            task,
            updates: { [roadmap.id]: { ...roadmap, publishedInfo: { publishedAt: roadmap.lastUpdatedAt } } },
            updateType: 'roadmaps',
        });
    },

    async unpublishRoadmap ({ getters, dispatch }, roadmapId) {
        const task = async () => {
            const updatedRoadmap = await api.unpublishRoadmap(roadmapId);

            dispatch('setOne', updatedRoadmap);

            return updatedRoadmap;
        };

        const roadmap = getters.roadmapById(roadmapId);

        return asyncQueue.enqueue({
            task,
            updates: { [roadmap.id]: { ...roadmap, publishedInfo: { id: '', publishedURL: '', publishedAt: 0 } } },
            updateType: 'roadmaps',
        });
    },

    async deleteRoadmap ({ commit }, roadmapId) {
        await api.deleteRoadmap(roadmapId);

        commit('deleteByKey', roadmapId);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
